import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Fragment, type ReactNode } from "react";
import { CardField } from "@/components/RecordCard";
import type { AgedBucket } from "@/lib/api";

// Shared bucket renderer for the aged-receivables / aged-payables JSON
// served by GET /finance/invoicing/reports/aged-{receivables,payables}
// (Current / 1-30 / 31-60 / 61-90 / 90+ buckets, integer-minor-unit
// `*_cents` amounts). Reimplemented for this app; upstream dubbl report
// pages are layout reference only and must not be copied.

export function AgedReportError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
    >
      {message}
      {message.includes("No Console organization context") ? (
        <p className="mt-1 text-[var(--muted-foreground)]">
          Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
        </p>
      ) : null}
      <div className="mt-3">
        <Button type="button" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

export interface AgedRowCells {
  key: string;
  number: string;
  name: string;
  dueDate: string;
  daysOverdue: number;
  amountDueCents: number;
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function overdueLabel(row: AgedRowCells): string {
  if (row.daysOverdue <= 0) return row.dueDate;
  return `${row.dueDate} · ${row.daysOverdue} ${
    row.daysOverdue === 1 ? "day" : "days"
  } overdue`;
}

export function AgedBucketsTable<TRow>({
  buckets,
  grandTotalCents,
  numberLabel,
  nameLabel,
  rowsFor,
  emptyMessage,
  numberLink,
}: {
  buckets: Array<AgedBucket<TRow>>;
  grandTotalCents: number;
  numberLabel: string;
  nameLabel: string;
  rowsFor: (bucket: AgedBucket<TRow>) => AgedRowCells[];
  emptyMessage: string;
  /** Renders the document number cell; used to drill into the document. */
  numberLink?: (row: AgedRowCells) => ReactNode;
}) {
  const rowCount = buckets.reduce(
    (sum, bucket) => sum + rowsFor(bucket).length,
    0,
  );
  return (
    <div className="grid gap-3">
      <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{numberLabel}</TableHead>
              <TableHead>{nameLabel}</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Days overdue</TableHead>
              <TableHead className="text-right">Amount due</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowCount === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>{emptyMessage}</TableCell>
              </TableRow>
            ) : (
              buckets.map((bucket) => {
                const rows = rowsFor(bucket);
                if (rows.length === 0) return null;
                return (
                  <Fragment key={`bucket-${bucket.label}`}>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="bg-[var(--muted)] text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]"
                      >
                        {bucket.label} — {bucket.count}{" "}
                        {bucket.count === 1 ? "document" : "documents"} ·{" "}
                        {money(bucket.total_cents)}
                      </TableCell>
                    </TableRow>
                    {rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell className="font-mono text-xs">
                          {numberLink ? numberLink(row) : row.number}
                        </TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {row.dueDate}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.daysOverdue}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(row.amountDueCents)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })
            )}
            {rowCount > 0 ? (
              <TableRow key="grand-total">
                <TableCell colSpan={4} className="font-bold">
                  Total outstanding
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">
                  {money(grandTotalCents)}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <div
        className="stack-list grid gap-3 min-[64rem]:hidden"
        data-testid="aged-buckets-stack"
      >
        {rowCount === 0 ? (
          <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4 text-sm text-[var(--muted-foreground)]">
            {emptyMessage}
          </article>
        ) : (
          <>
            {buckets.map((bucket) => {
              const rows = rowsFor(bucket);
              if (rows.length === 0) return null;
              return rows.map((row) => (
                <article
                  key={`${bucket.label}-${row.key}`}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title font-medium">
                      {numberLink ? numberLink(row) : row.number}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {money(row.amountDueCents)}
                    </span>
                  </div>
                  <CardField label={nameLabel}>{row.name}</CardField>
                  <CardField label="Bucket">{bucket.label}</CardField>
                  <CardField label="Due">{overdueLabel(row)}</CardField>
                  {numberLink ? (
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button asChild variant="secondary" size="sm">
                        {numberLink(row)}
                      </Button>
                    </div>
                  ) : null}
                </article>
              ));
            })}
            <article className="stack-card flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4 font-bold">
              <span>Total outstanding</span>
              <span className="tabular-nums">{money(grandTotalCents)}</span>
            </article>
          </>
        )}
      </div>
    </div>
  );
}
